import React, { useEffect, useMemo } from 'react';
import type { RovoConfigProviderEntryProps } from '@jira/ai-rovo_actions-shared-types/src/RovoConfigProviderEntry.types.tsx';
import type { RovoConfiguration } from '@jira/ai-rovo_actions-shared-types/src/RovoConfiguration.types.tsx';
import type { RovoAction } from '@jira/ai-rovo_actions-shared-types/src/RovoAction.types.tsx';
import { useWfmSkillPrompt } from '@jira/servicedesk__workforce-optimization/src/useWfmSkillPrompt.tsx';
import AiAgentIcon from '@atlaskit/icon/core/ai-agent';
import { defineMessage, type MessageDescriptor, useIntl } from '@atlassian/jira-intl';
import { expVal } from '@atlassian/jira-feature-experiments';
import { HandleActionType } from '@atlassian/rovo-conversation-actions/types';
import type { SchedulesRovoConfigProviderEntrypointProps } from './SchedulesRovoConfigProvider.entrypoint.types.tsx';

const explainSchedulesPrompt: MessageDescriptor = defineMessage({
	defaultMessage: 'Explain the schedules for this week',
	description: 'WFM AI prompt for explaining the schedules currently shown on the page.',
	id: 'servicedesk-schedules.rovo.explain-schedules',
});

const findCoverageGapsPrompt: MessageDescriptor = defineMessage({
	defaultMessage: 'Scan schedules and list coverage gaps',
	description:
		'AI prompt for identifying coverage gaps in the schedules currently shown on the page.',
	id: 'servicedesk-schedules.rovo.find-coverage-gaps',
});

const viewMyShiftsPrompt: MessageDescriptor = defineMessage({
	defaultMessage: 'Show my shifts for the visible date range and flag any conflicting shifts',
	description:
		'WFM AI prompt for listing the current user shifts in the visible date range and flagging any conflicting shifts.',
	id: 'servicedesk-schedules.rovo.view-my-shifts',
});

const deleteMyShiftPrompt: MessageDescriptor = defineMessage({
	defaultMessage: 'Delete my shift for ',
	description:
		'WFM AI prompt stem for deleting the current user shift. This is a write action, so the prompt is pre-filled into the chat input (not auto-sent) for the user to complete with the date range and team before sending.',
	id: 'servicedesk-schedules.rovo.delete-my-shift',
});

export const useSchedulesRovoConfig = (): RovoConfiguration => {
	const { formatMessage } = useIntl();
	const { formatWfmSkillPrompt } = useWfmSkillPrompt();

	return useMemo<RovoConfiguration>(
		() => ({
			getActions: async () => {
				if (!expVal('jsm-workforce-skill-experiment', 'isEnabled', false)) {
					return [];
				}

				const actions: RovoAction[] = [
					{
						action: {
							id: 'wfm-explain-schedules',
							title: formatMessage(explainSchedulesPrompt),
							staticPrompt: formatWfmSkillPrompt(explainSchedulesPrompt),
							icon: () => <AiAgentIcon label="" />,
							handleType: HandleActionType.Skill,
						},
					},
				];

				actions.push({
					action: {
						id: 'wfm-find-coverage-gaps',
						title: formatMessage(findCoverageGapsPrompt),
						staticPrompt: formatWfmSkillPrompt(findCoverageGapsPrompt),
						icon: () => <AiAgentIcon label="" />,
						handleType: HandleActionType.Skill,
					},
				});

				actions.push({
					action: {
						id: 'wfm-view-my-shifts',
						title: formatMessage(viewMyShiftsPrompt),
						staticPrompt: formatWfmSkillPrompt(viewMyShiftsPrompt),
						icon: () => <AiAgentIcon label="" />,
						handleType: HandleActionType.Skill,
					},
				});

				actions.push({
					action: {
						id: 'wfm-delete-my-shift',
						title: formatMessage(deleteMyShiftPrompt),
						// Write action: pre-fill the chat input (not auto-send) so the user can
						// complete the prompt with the specific date range / team before sending.
						dynamicPrompt: formatWfmSkillPrompt(deleteMyShiftPrompt),
						encodeDynamicPromptAsSkill: true,
						icon: () => <AiAgentIcon label="" />,
						handleType: HandleActionType.Chat,
					},
				});

				return Promise.all(
					actions.map(async ({ action, entryPointActions }) => {
						await entryPointActions?.load();
						return action;
					}),
				);
			},
			tags: expVal('jsm-workforce-skill-experiment', 'isEnabled', false)
				? { skillRegistryTags: ['manage-workforce'] }
				: { skillRegistryTags: [] },
		}),
		[formatMessage, formatWfmSkillPrompt],
	);
};

export const SchedulesRovoConfigProvider = ({
	setConfiguration,
}: SchedulesRovoConfigProviderEntrypointProps): null => {
	const config = useSchedulesRovoConfig();

	useEffect(() => {
		// eslint-disable-next-line react-you-might-not-need-an-effect/no-pass-data-to-parent
		setConfiguration(config);
	}, [config, setConfiguration]);

	return null;
};

export const SchedulesRovoConfigProviderEntry = ({
	props: { setConfiguration },
}: RovoConfigProviderEntryProps): React.JSX.Element => (
	<SchedulesRovoConfigProvider setConfiguration={setConfiguration} />
);

export default SchedulesRovoConfigProviderEntry;
